import React, { useContext, useEffect, useMemo } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { IntlProvider } from 'components/i18n';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import Theme, {
  ThemeProp,
  GlobalThemeTokens,
  colors,
} from '@atlaskit/theme';
import useMousetrap from 'react-hook-mousetrap';

import { QueryParamProvider } from 'use-query-params';
import { ModalProvider } from 'components/modal-dialog';
import BankAccountsPage from '../modules/bank/BankAccountsPage';
import BankTransactionsPage2 from '../modules/bank/BankTransactionsPage2.jsx'
import Playground from '../modules/playground/Playground';

import locale_en from '../translations/en.json';
import locale_nl from '../translations/nl.json';
import ngDeps from '../libs/ngDeps';
import { AuthProvider } from '../modules/auth';
import { CurrentCompanyProvider } from '../modules/auth/CurrentCompany';
import { NotificationProvider } from '../components/notification';
import { ReactQueryConfigProvider } from 'react-query';
import GeneralLedgerPage from '../modules/admin/GeneralLedgerPage';
import CompanyFinancialsPage from '../modules/admin/CompanyFinancialsPage';
import { useAuth } from '../modules/auth';
import CssReset from './CssReset';
import useCompanyNavigation from '../modules/auth/useCompanyNavigation';
import { NotesContext, NotesProvider } from "../modules/settings/Notes";
import { ChakraProvider } from "@chakra-ui/react";

const defaultFormats = {
  date: { default: { day: '2-digit', month: '2-digit', year: 'numeric' } },
};
const data = {
  nl: locale_nl,
  en: locale_en,
};

type Language = 'nl' | 'en';

interface Props {}

const CutomRoute: React.FC = ({ children }) => {
  // @ts-ignore
  return <Route element={children()} />;
};

const HotkeyProvider: React.FC<Props> = ({ children }) => {
  const navigate = useNavigate();
  const companyNavigate = useCompanyNavigation();
  const { isBookkeeper } = useAuth();
  const { toggleNotes } = useContext(NotesContext)

  useMousetrap(['b e', 'command b e'], ()=> {
    if (isBookkeeper) navigate('/boekhouder/bedrijven')
  })
  useMousetrap(['b a', 'command b a'], ()=> {
    companyNavigate('/bank')
  })
  useMousetrap(['i n b', 'command i n b'], ()=> {
    companyNavigate('/inbox')
  })
  useMousetrap(['i n k', 'command i n k'], ()=> {
    companyNavigate('/purchases')
  })
  useMousetrap(['v e', 'command v e'], ()=> {
    companyNavigate('/sale/invoices')
  })
  useMousetrap(['d a', 'd b', 'command d a', 'command d b'], ()=> {
    companyNavigate('/dashboard')
  })
  useMousetrap(['m e', 'command m e'], ()=> {
    companyNavigate('/memoriaal')
  })
  useMousetrap(['r a', 'command r a'], ()=> {
    companyNavigate('/rapportage/journalen')
  })
  useMousetrap(['n', 'command n'], ()=> {
    if (isBookkeeper) toggleNotes()
  })

  return <div>{children}</div>;
}


const CustomQueryParamProvider: React.FC<Props> = ({ children }) => {
  // navi
  const navigate = useNavigate();
  const location = useLocation();

  // console.log('CustomQueryParamProvider',window.location);
  // navigate('success', { replace: true })
  // console.log('CustomQueryParamProvider');
  // @ts-ignore
  const { $location, $rootScope, $timeout } = ngDeps;

  // console.log('$location',$location);
  // console.log('angular location',location);
  const history = React.useMemo(
    () => ({
      push: (newLocation: { pathname: any; query: any; search: any }) => {
        // console.log('newLocation',newLocation.pathname);
        if (!$timeout) return;
        $timeout(() => {
          $location.path($location.path()).search(newLocation.query);
          navigate($location.path() + newLocation.search);
          // const popStateEvent = new PopStateEvent('popstate');
          // dispatchEvent(popStateEvent);
        });
      },
      replace: (newLocation: { pathname: any; query: any; search: any }) => {
        if (!$timeout) return;
        $timeout(() => {
          $location.path($location.path()).search(newLocation.query).replace();
          navigate($location.path() + newLocation.search, { replace: true });
          // const popStateEvent = new PopStateEvent('popstate');
          //  dispatchEvent(popStateEvent);
        });
      },
    }),
    [location, navigate]
  );
  // navigate(location.pathname+ location.search, {replace: true}),

  // const reachHistory = {
  //   navigate,
  //   location,
  // }

  return (
    // @ts-ignore
    <QueryParamProvider history={history} location={location}>
      {' '}
      <div>{children}</div>{' '}
    </QueryParamProvider>
  );
};

const CompanyRoutes = () => {};

const App: React.FC<Props> = (props) => {
  const pathPrefix = '/react';

  let language: Language = navigator.language.split(/[-_]/)[0] as Language;

  // Similar to componentDidMount and componentDidUpdate:
  useEffect(() => {
    // Update the document title using the browser API
   // alert('load')
  });

  // language = 'nl';

  const darkTheme: ThemeProp<GlobalThemeTokens, any> = (theme, themeProps) => {
    return {
      ...theme(themeProps),
      mode: 'light',
      // backgroundColor: props.hover ? 'rebeccapurple' : 'palevioletred',
      // textColor: props.hover ? '#fff' : 'papayawhip',
    };
  };

  const queryConfig = useMemo(() => ({ queries: { refetchOnWindowFocus: false } }),[]);

  return (
    // <AtlaskitThemeProvider mode={"ligh"}>
    <div>
      <ChakraProvider resetCSS={false}>
      {CssReset}
    <Theme.Provider value={darkTheme}>
      <SkeletonTheme color={colors.N20} highlightColor={colors.N30}>
        <IntlProvider
          locale={language}
          messages={data[language]}
          formats={defaultFormats}
        >
            <ReactQueryConfigProvider config={queryConfig}>
          <NotificationProvider>
            <ModalProvider>
              <Router>
                  <CustomQueryParamProvider>
                    <AuthProvider>
                      <Routes>
                      {/*<Route path="r*" element={<Playground />} />*/}
                      <Route path="boekhouder/*" element={<Playground />} />
                        <Route path="boekhouder/admin/general-ledger" element={<GeneralLedgerPage />} />
                        <Route path="boekhouder/admin/company-financials" element={<CompanyFinancialsPage />} />
                      <Route
                        path=":companySlug/*"
                        element={
                          <CurrentCompanyProvider>
                            <NotesProvider>
                            <HotkeyProvider>
                              <Routes>
                              <Route
                                path="bank"
                                element={<BankAccountsPage />}
                              />
                                <Route
                                  path="bank/transactions/:bankAccountId"
                                  element={<BankTransactionsPage2 />}
                                />
                              <Route
                                path="react-app/*"
                                element={<Playground showNavigation/>}
                              />
                              <Route
                                path="*"
                                element={<Playground showNavigation={false}/>}
                              />
                            </Routes>
                            </HotkeyProvider>
                            </NotesProvider>
                          </CurrentCompanyProvider>
                        }
                      />
                    </Routes>
                    </AuthProvider>
                  </CustomQueryParamProvider>
              </Router>

          </ModalProvider>
          </NotificationProvider>
          </ReactQueryConfigProvider>
        </IntlProvider>
      </SkeletonTheme>
    </Theme.Provider>
      </ChakraProvider>
    </div>
    // </AtlaskitThemeProvider>
  );
};

// ReactDOM.render(
//   // <React.StrictMode>
//     <App />,
//   // </React.StrictMode>,
//   document.getElementById('root')
// );

export default App;
